import React, { useEffect, useState } from 'react';
import { contractsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Timer, Download, Filter, ChevronRight, ChevronDown, Truck, AlertTriangle, FileText } from 'lucide-react';

function pctColor(p) {
  if (p === null || p === undefined) return 'bg-slate-50 text-slate-400 border-slate-200';
  if (p >= 90) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (p >= 70) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-red-50 text-red-700 border-red-200';
}

export default function OtdReportPage() {
  const today = new Date();
  const defFrom = new Date(today.getFullYear(), today.getMonth() - 11, 1).toISOString().slice(0, 10);
  const defTo = today.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(defFrom);
  const [toDate, setToDate] = useState(defTo);
  const [customerId, setCustomerId] = useState('all');
  const [orderType, setOrderType] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    contractsAPI.activeCustomers().then(r => setCustomers(r.data || [])).catch(() => {});
    load();
    // eslint-disable-next-line
  }, []);

  const params = () => {
    const p = {};
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    if (customerId && customerId !== 'all') p.customer_id = customerId;
    if (orderType && orderType !== 'all') p.order_type = orderType;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await contractsAPI.otdReport(params());
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load OTD report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await contractsAPI.otdReportExport(params());
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OnTime_Delivery_Report_${defTo}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('OTD report exported');
    } catch (err) {
      toast.error('Failed to export OTD report');
    } finally {
      setExporting(false);
    }
  };

  const s = data?.summary || {};
  const pos = data?.purchase_orders || [];

  return (
    <div className="space-y-6" data-testid="otd-report-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] flex items-center gap-2">
            <Timer className="w-6 h-6 text-red-600" /> On-Time Delivery Report
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Quantity-weighted OTD across customer POs. Subcontract orders use job-work challan due dates vs dispatch of delivered trace IDs; direct orders use the PO due date. Excludes returns / rejection / quarantine.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}
          className="gap-1 border-red-200 text-red-700 hover:bg-red-50" data-testid="otd-export-button">
          <Download className="w-4 h-4" /> {exporting ? 'Exporting...' : 'Export Excel'}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs font-medium text-slate-500 mb-1 block">From</Label>
              <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-40 text-sm" data-testid="otd-from-date" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500 mb-1 block">To</Label>
              <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-40 text-sm" data-testid="otd-to-date" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500 mb-1 block">Customer</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="w-52 h-9 text-sm" data-testid="otd-customer-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500 mb-1 block">Order Type</Label>
              <Select value={orderType} onValueChange={setOrderType}>
                <SelectTrigger className="w-40 h-9 text-sm" data-testid="otd-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Subcontract">Subcontract</SelectItem>
                  <SelectItem value="Direct">Direct</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={load} disabled={loading} size="sm" className="bg-[#0F172A] hover:bg-[#1E293B] text-white gap-1.5" data-testid="otd-apply-filter">
              <Filter className="w-3.5 h-3.5" /> {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center mb-2"><Timer className="w-3.5 h-3.5 text-red-600" /></div>
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="otd-overall-pct">{s.otd_pct !== null && s.otd_pct !== undefined ? `${s.otd_pct}%` : 'N/A'}</p>
            <p className="text-xs text-slate-500">Overall OTD</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center mb-2"><Truck className="w-3.5 h-3.5 text-slate-600" /></div>
            <p className="text-2xl font-bold text-emerald-700">{s.on_time_qty || 0}</p>
            <p className="text-xs text-slate-500">On-Time Qty</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-amber-50 flex items-center justify-center mb-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /></div>
            <p className="text-2xl font-bold text-red-700">{s.late_qty || 0}</p>
            <p className="text-xs text-slate-500">Late Qty</p>
            <p className="text-[10px] text-amber-600 font-medium mt-0.5">{s.overdue_pending_qty || 0} overdue-pending</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center mb-2"><FileText className="w-3.5 h-3.5 text-blue-600" /></div>
            <p className="text-2xl font-bold text-blue-700">{s.total_pos || 0}</p>
            <p className="text-xs text-slate-500">Purchase Orders</p>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">{s.subcontract_pos || 0} sub / {s.direct_pos || 0} direct</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border border-slate-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold text-slate-500 w-8"></TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">PO Number</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Customer</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Type</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500">Due Date</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">On-Time</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">Late</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">Overdue Pending</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">OTD %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pos.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 text-sm py-8">No purchase orders for selected filters</TableCell></TableRow>
              )}
              {pos.map((po) => (
                <React.Fragment key={po.po_id}>
                  <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => setExpanded(prev => ({ ...prev, [po.po_id]: !prev[po.po_id] }))} data-testid={`otd-po-row-${po.po_number}`}>
                    <TableCell className="py-2">{expanded[po.po_id] ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}</TableCell>
                    <TableCell className="text-sm font-medium">{po.po_number}</TableCell>
                    <TableCell className="text-sm text-slate-600">{po.customer_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${po.order_type === 'Subcontract' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>{po.order_type}</Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-slate-500">{po.delivery_due_date || '—'}</TableCell>
                    <TableCell className="text-sm text-right font-mono text-emerald-600">{po.on_time_qty}</TableCell>
                    <TableCell className="text-sm text-right font-mono text-red-600">{po.late_qty}</TableCell>
                    <TableCell className="text-sm text-right font-mono text-amber-600">{po.overdue_pending_qty}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className={`text-[10px] ${pctColor(po.otd_pct)}`}>{po.otd_pct !== null && po.otd_pct !== undefined ? `${po.otd_pct}%` : 'N/A'}</Badge>
                    </TableCell>
                  </TableRow>
                  {expanded[po.po_id] && (po.lines || []).map((ln, i) => (
                    <TableRow key={`${po.po_id}-${i}`} className="bg-slate-50/40">
                      <TableCell></TableCell>
                      <TableCell colSpan={2} className="text-xs text-slate-600 pl-6">
                        ↳ {ln.ref}{ln.supplier_name ? ` (${ln.supplier_name})` : ''}
                        {ln.internal_ref ? <span className="text-slate-400"> · {ln.internal_ref}</span> : null}
                      </TableCell>
                      <TableCell className="text-[10px] text-slate-400">{po.order_type === 'Subcontract' ? 'challan' : 'line'}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-500">{ln.due_date || '—'}</TableCell>
                      <TableCell className="text-xs text-right font-mono text-emerald-600">{ln.on_time_qty}</TableCell>
                      <TableCell className="text-xs text-right font-mono text-red-600">{ln.late_qty}</TableCell>
                      <TableCell className="text-xs text-right font-mono text-amber-600">{ln.overdue_pending_qty}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className={`text-[9px] ${pctColor(ln.otd_pct)}`}>{ln.otd_pct !== null && ln.otd_pct !== undefined ? `${ln.otd_pct}%` : 'N/A'}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
